import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  PromoBanner,
  PromoBannerApiResponse,
  PromoBannerCollection,
  PromoBannerGroup,
  PromoBannerPageData,
  PromoBannerPosition,
  PromoBannerProduct,
  PromoBannerSaveRequest
} from '@app/model/promo-banner-management.model';
import { ProductTypeValueService } from '@app/api/api';

@Injectable({
  providedIn: 'root'
})
export class PromoBannerManagementDataService {
  private readonly bannerApiUrl = environment.backendhost + '/api/banner';

  private readonly bannerGroupApiUrl = environment.backendhost + '/api/bannerGroup';

  constructor(
    private http: HttpClient,
    private productTypeValueService: ProductTypeValueService
  ) {}

  getPromoBannerPageData(): Observable<PromoBannerPageData> {
    return this.getPromoBannerGroup().pipe(
      switchMap((group) => {
        if (!group) {
          return of({
            group: null,
            blogBanner: null,
            productBanner: null
          });
        }

        return this.getBannersByGroupId(group.id).pipe(
          map((banners) => ({
            group,
            blogBanner:
              banners.find((banner) => banner.position === PromoBannerPosition.Blog) || null,
            productBanner:
              banners.find((banner) => banner.position === PromoBannerPosition.Product) || null
          }))
        );
      })
    );
  }

  getPromoBannerGroup(): Observable<PromoBannerGroup | null> {
    return this.http
      .get<
        PromoBannerApiResponse<PromoBannerCollection<PromoBannerGroup>>
      >(this.bannerGroupApiUrl + '?Page=1&PageSize=100')
      .pipe(
        map((response) => {
          if (!response.success || !response.data || !Array.isArray(response.data.collection)) {
            return null;
          }

          return (
            response.data.collection.find(
              (group) => group.pageType === 1 && group.pageLocationType === 2
            ) || null
          );
        })
      );
  }

  getBannersByGroupId(groupId: string): Observable<PromoBanner[]> {
    return this.http
      .get<
        PromoBannerApiResponse<PromoBanner[]>
      >(this.bannerGroupApiUrl + '/' + encodeURIComponent(groupId) + '/banners')
      .pipe(
        map((response) => {
          if (!response.success || !Array.isArray(response.data)) {
            return [];
          }

          return response.data.slice().sort((firstBanner, secondBanner) => {
            if (firstBanner.is_active !== secondBanner.is_active) {
              return firstBanner.is_active ? -1 : 1;
            }

            return (firstBanner.sort_order || 0) - (secondBanner.sort_order || 0);
          });
        })
      );
  }

  createBanner(request: PromoBannerSaveRequest): Observable<PromoBanner> {
    return this.http
      .post<PromoBannerApiResponse<PromoBanner>>(this.bannerApiUrl, request)
      .pipe(map((response) => response.data));
  }

  updateBanner(bannerId: string, request: PromoBannerSaveRequest): Observable<PromoBanner> {
    const formData = new FormData();

    const appendField = (key: string, value: any): void => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      formData.append(key, '' + value);
    };

    appendField('slug', request.slug);
    appendField('link', request.link);
    appendField('position', request.position);
    appendField('sort_order', request.sort_order);
    appendField('add_info', request.add_info);
    appendField('banner_group_id', request.banner_group_id);
    appendField('reference_type', request.reference_type);
    appendField('reference_id', request.reference_id);
    appendField('title', request.title);
    appendField('description', request.description);
    appendField('start_date', request.start_date);
    appendField('end_date', request.end_date);

    formData.append('is_active', request.is_active === true ? 'true' : 'false');

    return this.http
      .put<
        PromoBannerApiResponse<PromoBanner>
      >(this.bannerApiUrl + '/' + encodeURIComponent(bannerId), formData)
      .pipe(map((response) => response.data));
  }

  uploadBannerImage(bannerId: string, file: File): Observable<PromoBanner> {
    const formData = new FormData();

    formData.append('file', file);

    return this.http
      .put<
        PromoBannerApiResponse<PromoBanner>
      >(this.bannerApiUrl + '/' + encodeURIComponent(bannerId) + '/upload-image', formData)
      .pipe(map((response) => response.data));
  }

  searchProducts(
    keyword: string,
    page: number = 1,
    pageSize: number = 10
  ): Observable<PromoBannerCollection<PromoBannerProduct>> {
    const searchText = (keyword || '').trim();

    const filters = searchText ? 'name|sapatco_code|product_code|sku@=' + searchText : '';

    return this.productTypeValueService.apiFullProductsGet(filters, '', page, pageSize).pipe(
      map((response: any) => {
        if (!response || !response.success || !response.data) {
          return {
            collection: [],
            total: 0,
            pagesize: pageSize,
            pageIndex: page
          };
        }

        // /api/fullProducts returns collection items shaped as { product: {...}, type_values: [...] }
        const rawItems = Array.isArray(response.data.collection) ? response.data.collection : [];
        const products = rawItems.map((item: any) =>
          item && item.product ? { ...item.product, type_values: item.type_values || [] } : item
        );

        return {
          collection: products.filter((product: PromoBannerProduct) => product.is_hidden !== true),
          total: response.data.total || 0,
          pagesize: response.data.pagesize || pageSize,
          pageIndex: response.data.pageIndex || page
        };
      })
    );
  }

  deleteBanner(bannerId: string): Observable<any> {
    return this.http.delete(this.bannerApiUrl + '/' + encodeURIComponent(bannerId));
  }
}
